import { classes } from './Widget.st.css';
import React, { FC, useState, useRef, useLayoutEffect } from 'react';
import {
  TitleSection,
  LogoSection,
  DividerSection,
  ErrorSection,
  FormSwitchBarSection,
} from '../../common';
import { useStyles } from '@wix/tpa-settings/react';
import { ControllerProps } from './types';
import layoutParams from '../layoutParams';
import { SocialButtonsSection } from '../../common/SocialButtonsSection/SocialButtonsSection';
import { LoginFormSection } from './FormSection/LoginFormSection';
import { SocialButtonsPlacement } from '../Settings/Tabs/LayoutTab/SocialPreset';
import { useRepromise } from '../../common/repromise';
import { useConnectionSettings } from '../../common/useConnectionSettings';
import { ForcedState, TEXT_DIRECTION } from '../../../constants';
import { Close } from '@wix/wix-ui-icons-common/on-stage';
import stylesParams from '../stylesParams';
import classNames from 'classnames';
import { IconButton, IconButtonThemes } from 'wix-ui-tpa/cssVars';
import { useStyleParamLogger } from '../../utils/styleParamLogger';
import { useSettingsParamLogger } from '../../utils/settingsParamLogger';
import { useLayoutParamLogger } from '../../utils/layoutParamLogger';
import settingsParams from '../settingsParams';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { getSidesPaddingDefault } from '../../../constants/layoutConstants';

const Widget: FC<ControllerProps> = (props) => {
  const { isEditor, isEditor3, isStudio2, isPreview, forceView, settings } =
    props;
  const { isRTL, isMobile } = useEnvironment();
  const { t } = useTranslation();
  // All functions passed as props must be wrapped with repromise
  // They must be unwrapped with useRepromise before normal use
  const login = useRepromise(props.login, props);
  const loginWithIdp = useRepromise(props.loginWithIdp, props);
  const promptAuthPage = useRepromise(props.promptAuthPage, props);
  const closeLightbox = useRepromise(props.closeLightbox, props);
  const promptLogin = useRepromise(props.promptLogin, props);

  const [error, setError] = useState<string | undefined>();
  const lightboxRef = useRef<HTMLDivElement>(null);
  const styles = useStyles();

  // Monitor all style parameter changes
  useStyleParamLogger(stylesParams);

  // Monitor all settings parameter changes
  useSettingsParamLogger(settingsParams);

  // Monitor all layout parameter changes
  useLayoutParamLogger(layoutParams);

  const shouldShowSocialButtonsFirst =
    styles.get(layoutParams.socialButtonsFirst) ===
    SocialButtonsPlacement.FIRST;

  const { socialConnections, hasPasswordConnection, ssoMandatory } =
    useConnectionSettings({
      isEditor,
      settings,
    });

  // Determine which section should get auto-focus based on layout
  const shouldFormAutoFocus =
    hasPasswordConnection && !shouldShowSocialButtonsFirst;
  const shouldSocialAutoFocus =
    socialConnections.length > 0 && shouldShowSocialButtonsFirst;
  const shouldLightboxAutoFocus =
    !shouldFormAutoFocus && !shouldSocialAutoFocus;

  useLayoutEffect(() => {
    if (shouldLightboxAutoFocus && !isEditor) {
      requestAnimationFrame(() => {
        lightboxRef.current?.focus();
      });
    }
  }, [shouldLightboxAutoFocus, isEditor]);

  const formSection = hasPasswordConnection ? (
    <LoginFormSection
      key="form"
      login={login}
      setError={setError}
      isEditor={isEditor}
      isPreview={isPreview}
      forceView={forceView}
      recaptchaOption={settings.collectionSettings?.loginRecaptchaOption}
      promptAuthPage={promptAuthPage}
      allowPostAuthRedirect={props.allowPostAuthRedirect}
      autoFocus={shouldFormAutoFocus}
    />
  ) : null;

  const dividerSection =
    hasPasswordConnection && socialConnections.length > 0 ? (
      <DividerSection key="divider" formType="login" />
    ) : null;

  const socialSection =
    socialConnections.length > 0 ? (
      <SocialButtonsSection
        key="social"
        enabledConnections={socialConnections}
        loginWithIdp={loginWithIdp}
        setError={setError}
        isEditor={isEditor}
        isPreview={isPreview}
        forceView={forceView}
        layoutParams={layoutParams}
        settingsParams={settingsParams}
        stylesParams={stylesParams}
        autoFocus={shouldSocialAutoFocus}
      />
    ) : null;

  const sections: React.ReactNode[] = [];
  if (formSection) {
    sections.push(
      <div key="form" className={classes.formOrder}>
        {formSection}
      </div>,
    );
  }
  if (dividerSection) {
    sections.push(
      <div key="divider" className={classes.dividerOrder}>
        {dividerSection}
      </div>,
    );
  }
  if (socialSection) {
    sections.push(
      <div key="social" className={classes.socialOrder}>
        {socialSection}
      </div>,
    );
  }

  const clickOnSignUp = () => {
    promptLogin({
      mode: 'signup',
      allowPostAuthRedirect: props.allowPostAuthRedirect,
    });
  };

  const errorMessage =
    isEditor && forceView === ForcedState.GLOBAL_ERROR
      ? t('memberAuthentication.common.errorSection.editorPreview')
      : error;
  const shouldShowErrorSection = Boolean(errorMessage);
  const shouldUseBuilderPopupDefaultSidesPadding =
    (isEditor3 || isStudio2) &&
    styles.get(layoutParams.sidesPadding) ===
      getSidesPaddingDefault({ isMobile });

  return (
    <div
      ref={lightboxRef}
      tabIndex={-1}
      data-hook="lightbox"
      aria-label={t(
        'memberAuthentication.ariaLabel.uouScreens.loginForm.popup',
      )}
      className={classNames(classes.lightbox, {
        [classes.shadow]: styles.get(stylesParams.enableShadow),
        [classes.editor3DefaultDesktopSidesPadding]:
          shouldUseBuilderPopupDefaultSidesPadding && !isMobile,
        [classes.editor3DefaultMobileSidesPadding]:
          shouldUseBuilderPopupDefaultSidesPadding && isMobile,
      })}
    >
      <div
        data-hook="login-widget"
        className={classNames(classes.content, {
          [classes.contentEditor]: isEditor,
        })}
      >
        {!(isEditor3 || isStudio2) && (
          <IconButton
            icon={<Close />}
            // Prevent focus change on pointer down so the input doesn't blur before this action
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => closeLightbox()}
            className={classes.closeButton}
            data-hook="login-close-button"
            theme={IconButtonThemes.Box}
            dir={isRTL ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR}
            aria-label={t(
              'memberAuthentication.ariaLabel.uouScreens.closeButton',
            )}
          />
        )}
        <LogoSection formType="login" />
        <TitleSection formType="login" ssoMandatory={ssoMandatory} />
        {/* Only show form switch bar if SSO is not mandatory - when SSO is mandatory,
          it doesn't make sense to redirect users to signup since they must use SSO */}
        {!ssoMandatory && (
          <FormSwitchBarSection formType="login" onSwitchForm={clickOnSignUp} />
        )}
        {sections.map((section) => section)}
        {shouldShowErrorSection && (
          <div className={classes.errorSection}>
            <ErrorSection error={errorMessage} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Widget;
